import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Users, Settings, BarChart3,
  Plus, Edit3, Trash2, Eye, Target,
  AlertCircle, CheckCircle, Database,
  ChevronDown, UserCircle, LogOut, FileText,
  RefreshCw, XCircle, Upload, X, Paperclip,
  Download, MapPin, Folder, FolderPlus, MoreHorizontal, CalendarDays, Bot
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Papa from 'papaparse';
import { urlPreviewUtils } from '../utils/urlPreview';
import { templateUtils, useTemplateManager } from '../utils/templateUtils';
import { getProducerName } from '../utils/userUtils';
import ManualImportModal from './ManualImportModal';
import FolderManager from './FolderManager';
import EmailTemplateFormFields from './EmailTemplateFormFields';
import {
  ProducerDashboard,
  LeadSources,
  EmailCampaigns,
  ContactManagement,
  ResponseHandler,
  WebsiteForms,
  ScrapingPreview,
  Notifications
} from './DashboardComponents';
import ScrapingPreviewModal from './ScrapingPreviewModal';
import ImportEmailTemplates from './ImportEmailTemplates';
import SettingsPage from './SettingsPage';
import CampaignCalendar from './CampaignCalendar';
import AssistantWorkspace from './AssistantWorkspace';
import { PrivacyPolicyContent } from '../pages/PrivacyPolicyPage';
import { TermsOfServiceContent } from '../pages/TermsOfServicePage';
import logoWhite from '../assets/logo-white.svg';

// Utility function to convert HTML to plain text for previews
const htmlToPlainText = (html) => {
  if (!html) return '';

  // First decode HTML entities
  let decoded = html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Convert common HTML elements to readable text
  decoded = decoded
    .replace(/<br\s*\/?>/gi, ' ') // Line breaks to spaces
    .replace(/<\/p>/gi, ' ') // Paragraph endings to spaces
    .replace(/<li[^>]*>/gi, '• ') // List items to bullets
    .replace(/<\/li>/gi, ' ') // List item endings to spaces
    .replace(/<[^>]+>/g, '') // Remove all remaining HTML tags
    .replace(/\s+/g, ' ') // Multiple spaces to single space
    .trim();

  return decoded;
};


// Email Templates Component
const PAGE_SIZE = 20;

const EmailTemplates = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [showFolderDropdown, setShowFolderDropdown] = useState(null);
  const [folders, setFolders] = useState([]); // Keep for dropdown references
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [folderCounts, setFolderCounts] = useState({});

  // Use the shared template manager hook
  const {
    loading,
    formData,
    resetForm,
    initializeEditForm,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    handleInputChange,
    handleFileSelect,
    handleRemoveAttachment,
    linkPreviews,
    loadingPreview,
    attachments,
    uploadingFile,
    templateUtils: utils,
    templates,
    loadTemplates: loadTemplatesRaw
  } = useTemplateManager();

  const { user } = useAuth();

  // Add local state for loading states not provided by the hook
  const [, setLoading] = useState(false);

  const loadTemplates = async (page = currentPage, folderId = selectedFolderId) => {
    const result = await loadTemplatesRaw({ page, limit: PAGE_SIZE, folderId });
    if (result) {
      setTotalTemplates(result.total);
      setFolderCounts(result.folderCounts);
      setCurrentPage(page);
    }
  };

  // Load templates and folders on component mount
  useEffect(() => {
    loadTemplates(1);
    loadFolders();

    // Listen for folder refresh events
    const handleFoldersRefresh = (event) => {
      console.log('Received folders refresh event, updating UI...');
      loadFolders();
    };

    window.addEventListener('foldersRefresh', handleFoldersRefresh);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('foldersRefresh', handleFoldersRefresh);
    };
  }, []);

  // Load folders for dropdown references
  const loadFolders = async () => {
    try {
      const response = await axios.get('/api/folders?folder_type=template');
      setFolders(response.data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.folder-dropdown-container')) {
        setShowFolderDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);


  // Move template to folder
  const handleMoveTemplateToFolder = async (templateId, folderId) => {
    try {
      const response = await axios.patch(`/api/folders/move-item`, {
        item_id: templateId,
        item_type: 'template',
        folder_id: folderId
      });

      if (response.data.success) {
        // Refresh templates to update the UI
        loadTemplates();
      }
    } catch (error) {
      console.error('Error moving template to folder:', error);
      alert('Failed to move template to folder');
    }
  };

  // Populate form when editing template
  useEffect(() => {
    if (editingTemplate) {
      initializeEditForm(editingTemplate);
    }
  }, [editingTemplate]);

  const handleCreateTemplate = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      await createTemplate();
      alert('Template created successfully!');
      setShowCreateModal(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      console.error('Failed to create template:', error);
      alert('Failed to create template: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      setLoading(true);
      await deleteTemplate(templateId);
      alert('Template deleted successfully!');
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateTemplate = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      await updateTemplate(editingTemplate.id);
      alert('Template updated successfully!');
      setEditingTemplate(null);
      resetForm();
      loadTemplates();
    } catch (error) {
      console.error('Failed to update template:', error);
      alert('Failed to update template: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditingTemplate(null);
    resetForm();
  };
  const totalPages = Math.ceil(totalTemplates / PAGE_SIZE);

  if (!showCreateModal && !editingTemplate) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Email Templates</h2>
            <p className="text-gray-600">Manage email templates for different lead types</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={20} />
            New Template
          </button>
        </div>

        {/* Two-column layout: Folders sidebar + Templates */}
        <div className="flex gap-6">
          {/* Folders Sidebar */}
          <FolderManager
            items={templates}
            itemFolderIdField="folder_id"
            itemCounts={folderCounts}
            selectedFolderId={selectedFolderId}
            onFolderSelect={(folderId) => {
              setSelectedFolderId(folderId);
              setCurrentPage(1);
              loadTemplates(1, folderId);
            }}
            allItemsLabel="All Templates"
            folderType="template"
          />

          {/* Templates Content */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                {loading && templates.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500">Loading templates...</div>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500">
                      {selectedFolderId
                        ? `No templates found in this folder.`
                        : 'No email templates found. Create your first template!'
                      }
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.map(template => (
                  <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {template.type}
                          </span>
                          {template.source === 'campaign_generated' && (
                            <span className="inline-block px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                              From Campaign
                            </span>
                          )}
                          {template.source === 'manual_import' && (
                            <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              Imported
                            </span>
                          )}
                          {template.folder_id && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              <Folder size={12} />
                              {folders.find(f => f.id === template.folder_id)?.name || 'Unknown'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="relative folder-dropdown-container">
                          <button
                            onClick={() => setShowFolderDropdown(showFolderDropdown === template.id ? null : template.id)}
                            className="p-2 text-gray-400 hover:text-blue-600"
                            title="Move to folder"
                          >
                            <MoreHorizontal size={16} />
                          </button>
                          {showFolderDropdown === template.id && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                              <div className="py-1">
                                <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b">
                                  Move to folder
                                </div>
                                <button
                                  onClick={() => {
                                    handleMoveTemplateToFolder(template.id, null);
                                    setShowFolderDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                >
                                  <div className="flex items-center gap-2">
                                    <Folder size={14} />
                                    No folder
                                  </div>
                                </button>
                                {folders.map(folder => (
                                  <button
                                    key={folder.id}
                                    onClick={() => {
                                      handleMoveTemplateToFolder(template.id, folder.id);
                                      setShowFolderDropdown(null);
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                      template.folder_id === folder.id ? 'bg-blue-50 text-blue-700' : ''
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Folder size={14} />
                                      {folder.name}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => setEditingTemplate(template)}
                          className="p-2 text-gray-400 hover:text-blue-600"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template.id)}
                          className="p-2 text-gray-400 hover:text-red-600"
                          disabled={loading}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Subject:</p>
                        <p className="text-sm text-gray-600">{template.subject}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Preview1:</p>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {htmlToPlainText(template.body)}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                      <div className="flex gap-4 text-xs text-gray-500">
                        <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                        {template.last_used_at && (
                          <span>Last used: {new Date(template.last_used_at).toLocaleDateString()}</span>
                        )}
                        <span>Used {template.usage_count || 0} times</span>
                      </div>
                    </div>
                  </div>
                ))}
                  </div>
                )}
              </div>
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadTemplates(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <div className="flex gap-1 items-center">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => loadTemplates(pageNum)}
                            className={`px-3 py-2 text-sm rounded-lg ${pageNum === currentPage ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => loadTemplates(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    );
  }
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">{editingTemplate ? 'Edit Template' : 'Create New Template'}</h2>
          <button
            onClick={handleCloseModal}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} className="space-y-4">
          <EmailTemplateFormFields
            formData={formData}
            showTemplateHeader={true}
            showTemplateType={formData.target_type != "scraping_preview"}
            onInputChange={handleInputChange}
            onFileSelect={handleFileSelect}
            onRemoveAttachment={handleRemoveAttachment}
            linkPreviews={linkPreviews}
            loadingPreview={loadingPreview}
            attachments={attachments}
            uploadingFile={uploadingFile}
            disabled={loading}
          />

          {/* Folder Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Folder (Optional)
            </label>
            <select
              value={formData.folder_id || ''}
              onChange={(e) => handleInputChange('folder_id', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No folder</option>
              {folders.map(folder => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleCloseModal}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (editingTemplate ? 'Updating...' : 'Saving...') : (editingTemplate ? 'Update Template' : 'Save Template')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TAB_KEYS = [
  'dashboard',
  'leads',
  'preview',
  'campaigns',
  'campaign-calendar',
  'templates',
  'contacts',
  'import-contacts',
  'import-templates',
  'assistant',
  'responses',
  'forms',
  'settings',
  'privacy',
  'terms'
];
const DEFAULT_TAB = 'dashboard';

const getInitialTab = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_TAB;
  }
  const params = new URLSearchParams(window.location.search);
  const requestedTab = params.get('tab');
  return requestedTab && TAB_KEYS.includes(requestedTab) ? requestedTab : DEFAULT_TAB;
};

const EmailMarketingPortal = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [currentProducer, setCurrentProducer] = useState(() => {
    // Set initial producer based on logged-in user info
    return user || null;
  });
  const [showProducerDropdown, setShowProducerDropdown] = useState(false);
  const [zohoConnected, setZohoConnected] = useState(false);
  const [, setZohoStatus] = useState(null);
  const [gmailStatus, setGmailStatus] = useState({ hasAccounts: false, accounts: [], primaryAccount: null });
  const [campaignToOpen, setCampaignToOpen] = useState(null);
  const derivedTeamId = user?.teamId ?? user?.team_id ?? null;
  //console.log('Derived team ID:', derivedTeamId, user);
  const isZohoEnabledTeam = Number(derivedTeamId) === 1;
  const location = useLocation();
  const navigate = useNavigate();

  const handleOpenCalendarCampaign = useCallback(campaign => {
    setCampaignToOpen(campaign);
    setActiveTab('campaigns');
  }, []);

  const handleCalendarCampaignOpened = useCallback(() => {
    setCampaignToOpen(null);
  }, []);

  // Global modal state for ScrapingPreviewModal
  const [showGlobalPreviewModal, setShowGlobalPreviewModal] = useState(false);
  const [globalPreviewData, setGlobalPreviewData] = useState(null);
  const [isConfirmingGlobal, setIsConfirmingGlobal] = useState(false);

  // State for dynamically loaded producers
  const [producers, setProducers] = useState([]);
  const [loadingProducers, setLoadingProducers] = useState(true);


  const [websiteForms] = useState([
  ]);

  const [campaigns] = useState([
  ]);

  const [stats] = useState({
  });

  // Function to get producer color (default to blue)
  const getProducerColor = (producer) => {
    if (producer.username.toLowerCase() === 'kelly') return 'blue';
    if (producer.username.toLowerCase() === 'angela') return 'green';
    if (producer.username.toLowerCase() === 'chelsie') return 'purple';
    // You can customize colors for specific producers here if needed
    // For now, return default blue for all producers
    return 'gray';
  };


  // Update current producer when user loads
  useEffect(() => {
    if (user?.id) {
      setCurrentProducer(user);
    }
  }, [user]);

  // Check Zoho connection status on component mount
  useEffect(() => {
    checkZohoStatus();
    checkGmailStatus();
    fetchProducers();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const resolvedTab = requestedTab && TAB_KEYS.includes(requestedTab) ? requestedTab : DEFAULT_TAB;
    setActiveTab(prev => (prev === resolvedTab ? prev : resolvedTab));
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasValidTab = TAB_KEYS.includes(activeTab);
    const currentParam = params.get('tab');

    if (hasValidTab && activeTab !== DEFAULT_TAB) {
      if (currentParam === activeTab) {
        return;
      }
      params.set('tab', activeTab);
    } else if (!hasValidTab || activeTab === DEFAULT_TAB) {
      if (!currentParam) {
        return;
      }
      params.delete('tab');
    }

    const searchString = params.toString();
    navigate(`${location.pathname}${searchString ? `?${searchString}` : ''}`, { replace: true });
  }, [activeTab, location.pathname, navigate]);

  const checkZohoStatus = async () => {
    try {
      const response = await axios.get('/api/zoho/token-status');
      setZohoStatus(response.data);
      setZohoConnected(response.data.connected && !response.data.isExpired);
    } catch (error) {
      console.error('Failed to check Zoho status:', error);
      setZohoConnected(false);
    }
  };

  const checkGmailStatus = async () => {
    try {
      const response = await axios.get('/api/gmail/accounts');
      const status = {
        hasAccounts: response.data.accounts.length > 0,
        accounts: response.data.accounts,
        primaryAccount: response.data.accounts.find(acc => acc.is_primary)
      };
      setGmailStatus(status);
      return status;
    } catch (error) {
      console.error('Failed to check Gmail status:', error);
      const fallbackStatus = { hasAccounts: false, accounts: [], primaryAccount: null };
      setGmailStatus(fallbackStatus);
      return fallbackStatus;
    }
  };

  const fetchProducers = async () => {
    try {
      setLoadingProducers(true);
      const response = await axios.get('/api/contact-management/producers');

      if (response.data.success) {
        // Store all raw producer information from server
        let producerList = response.data.producers;

        // Add current user if they're not a producer
        if (user?.role !== 'producer') {
          producerList = [...producerList, {
            id: user.id,
            username: user.username,
            display_name: user.display_name,
            email: user.email,
            role: user.role,
            team_id: user.team_id,
            location: user.location || user.country
          }].filter(Boolean);
        }
        console.log('Fetched producers:', producerList);
        setProducers(producerList);
      }
    } catch (error) {
      console.error('Failed to fetch producers:', error);
      // Fallback to empty array on error
      setProducers([]);
    } finally {
      setLoadingProducers(false);
    }
  };

  const connectToZoho = async () => {
    try {
      const response = await axios.get('/api/zoho/auth-url');
      window.open(response.data.authUrl, '_blank', 'width=600,height=700');

      // Listen for the callback completion
      const checkInterval = setInterval(async () => {
        try {
          await checkZohoStatus();
          if (zohoConnected) {
            clearInterval(checkInterval);
          }
        } catch (error) {
          console.error('Error checking status:', error);
        }
      }, 2000);

      // Clear interval after 5 minutes
      setTimeout(() => clearInterval(checkInterval), 300000);
    } catch (error) {
      console.error('Failed to get Zoho auth URL:', error);
    }
  };

  const disconnectZoho = async () => {
    try {
      await axios.delete('/api/zoho/disconnect');
      setZohoConnected(false);
      setZohoStatus(null);
    } catch (error) {
      console.error('Failed to disconnect Zoho:', error);
    }
  };



  const connectToGmail = async () => {
    try {
      const response = await axios.get('/api/gmail/auth-url');
      window.open(response.data.authUrl, '_blank', 'width=600,height=700');

      // Listen for the callback completion
      const checkInterval = setInterval(async () => {
        try {
          await checkGmailStatus();
          if (gmailStatus.hasAccounts) {
            clearInterval(checkInterval);
          }
        } catch (error) {
          console.error('Error checking Gmail status:', error);
        }
      }, 2000);

      // Clear interval after 5 minutes
      setTimeout(() => clearInterval(checkInterval), 300000);
    } catch (error) {
      console.error('Failed to get Gmail auth URL:', error);
    }
  };

  // Global modal handler functions
  const showGlobalScrapingPreview = (apolloResponseData) => {
    console.log('Global modal triggered with data:', apolloResponseData);
    setGlobalPreviewData(apolloResponseData);
    setShowGlobalPreviewModal(true);
  };

  const handleCloseGlobalModal = () => {
    setShowGlobalPreviewModal(false);
    setGlobalPreviewData(null);
  };

  const handleConfirmGlobalLeads = async (selectedLeads, templateId) => {
    setIsConfirmingGlobal(true);
    try {
      const response = await axios.post('/api/apollo/confirm-leads', {
        jobId: globalPreviewData.jobId,
        confirmedLeads: selectedLeads,
        templateId: templateId
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        setShowGlobalPreviewModal(false);
        setGlobalPreviewData(null);
        alert(`Successfully saved ${selectedLeads.length} leads to scraping preview!`);
      }
    } catch (error) {
      console.error('Failed to save leads:', error);
      alert('Failed to save confirmed leads. Please try again.');
    } finally {
      setIsConfirmingGlobal(false);
    }
  };

  // Set up global function - no cleanup to ensure it persists
  useEffect(() => {
    window.showScrapingPreviewModal = showGlobalScrapingPreview;
  }, []);

  const Header = () => (
    <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
      <div className="flex items-center gap-4">
        {/* Connection Status */}
        <div className="flex items-center gap-3">
        {/* Zoho Connection */}
        {isZohoEnabledTeam && (
          zohoConnected ? (
            <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              <CheckCircle size={14} />
              <span>Zoho Connected</span>
              <button
                onClick={disconnectZoho}
                className="ml-2 text-red-600 hover:text-red-800"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={connectToZoho}
              className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm hover:bg-red-200"
            >
              <XCircle size={14} />
              <span>Connect Zoho</span>
            </button>
          )
        )}

          {/* Gmail Connection */}
          {gmailStatus.hasAccounts ? (
            (() => {
              const activeAccounts = gmailStatus.accounts.filter(acc => acc.is_active !== 0);
              const inactiveAccounts = gmailStatus.accounts.filter(acc => acc.is_active === 0);
              const hasInactiveAccounts = inactiveAccounts.length > 0;

              return hasInactiveAccounts ? (
                <button
                  onClick={() => setActiveTab('settings')}
                  className="flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer transition-colors"
                  title="Click to go to settings and reconnect inactive accounts"
                >
                  <AlertCircle size={14} />
                  <span>
                    Gmail: {activeAccounts.length} active, {inactiveAccounts.length} inactive
                    {gmailStatus.primaryAccount && ` (${gmailStatus.primaryAccount.email})`}
                  </span>
                </button>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700">
                  <CheckCircle size={14} />
                  <span>
                    Gmail: {activeAccounts.length} active
                    {gmailStatus.primaryAccount && ` (${gmailStatus.primaryAccount.email})`}
                  </span>
                </div>
              );
            })()
          ) : (
            <button
              onClick={connectToGmail}
              className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200"
            >
              <Mail size={14} />
              <span>Connect Gmail</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">


        <div className="relative">
          <button
            onClick={() => setShowProducerDropdown(!showProducerDropdown)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border bg-${getProducerColor(currentProducer)}-50 text-${getProducerColor(currentProducer)}-700 border-${getProducerColor(currentProducer)}-200 cursor-pointer hover:opacity-80`}
          >
            <UserCircle size={20} />
            <span className="font-medium">{getProducerName(currentProducer)}</span>
            <ChevronDown size={16} />
          </button>

          {showProducerDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50">
              {loadingProducers ? (
                <div className="px-4 py-2 text-gray-500">Loading producers...</div>
              ) : (
                producers.map(producer => (
                  <button
                    key={producer.id}
                    onClick={() => {
                      setCurrentProducer(producer);
                      setShowProducerDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <UserCircle size={16} />
                    {getProducerName(producer)}
                    {producer.id === currentProducer?.id && <CheckCircle size={14} className="ml-auto text-green-500" />}
                  </button>
                ))
              )}
              <div className="border-t">
                <button className="w-full text-left px-4 py-2 hover:bg-gray-50 text-gray-600 flex items-center gap-2">
                  <Plus size={16} />
                  Add Producer
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <Notifications />

        <div className="flex flex-col items-end text-sm text-gray-600">
          <span>Welcome, {user?.username}</span>
          <button
            onClick={logout}
            className="flex items-center gap-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded mt-1"
          >
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  const ImportContacts = () => {
    const [csvData, setCsvData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({});
    const [preview, setPreview] = useState([]);
    const [showAllRecords, setShowAllRecords] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [tags, setTags] = useState('');
    const [leadStatus, setLeadStatus] = useState('');
    const [showManualImportModal, setShowManualImportModal] = useState(false);
    const [isDraggingCsv, setIsDraggingCsv] = useState(false);
    const [csvFileError, setCsvFileError] = useState('');

    const dbFields = [
      { value: 'company', label: 'Company' },
      { value: 'first_name', label: 'First Name' },
      { value: 'last_name', label: 'Last Name' },
      { value: 'full_name', label: 'Full Name (split into first/last)' },
      { value: 'email', label: 'Email' },
      { value: 'phone', label: 'Phone' },
      { value: 'job_title', label: 'Job Title' },
      { value: 'location', label: 'Location' },
      { value: 'country', label: 'Country' },
      { value: 'location_country', label: 'Location,Country (split into location/country)' },
      { value: 'notes', label: 'Notes' },
      { value: 'source', label: 'Source' },
      { value: '_skip', label: '-- Skip Column --' }
    ];

    const splitFullName = (fullName) => {
      if (!fullName || typeof fullName !== 'string') {
        return { first_name: '', last_name: '' };
      }

      const trimmed = fullName.trim();
      const parts = trimmed.split(/\s+/);

      if (parts.length === 0) {
        return { first_name: '', last_name: '' };
      } else if (parts.length === 1) {
        return { first_name: parts[0], last_name: '' };
      } else if (parts.length === 2) {
        // Two parts: first and last name
        return { first_name: parts[0], last_name: parts[1] };
      } else {
        // Three or more parts: extract first and last name, discard middle names/initials
        const firstName = parts[0]; // First part only
        const lastName = parts[parts.length - 1]; // Last part only
        return { first_name: firstName, last_name: lastName };
      }
    };

    const splitLocationCountry = (locationCountry) => {
      if (!locationCountry || typeof locationCountry !== 'string') {
        return { location: '', country: '' };
      }

      const trimmed = locationCountry.trim();
      const parts = trimmed.split(',');

      if (parts.length === 0) {
        return { location: '', country: '' };
      } else if (parts.length === 1) {
        return { location: parts[0].trim(), country: '' };
      } else {
        // First part is location, last part is country
        const location = parts[0].trim();
        const country = parts[parts.length - 1].trim();
        return { location: location, country: country };
      }
    };

    const processCsvFile = (file) => {
      if (!file) return;

      if (!file.name.toLowerCase().endsWith('.csv')) {
        setCsvFileError('Select one CSV file.');
        return;
      }

      setCsvFileError('');
      setImportResult(null);

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const parsedHeaders = results.meta.fields || [];
          setHeaders(parsedHeaders);
          setCsvData(results.data);

          // Auto-map common fields
          const autoMapping = {};
          parsedHeaders.forEach(header => {
            const lowerHeader = header.toLowerCase().trim();
            if (lowerHeader.includes('email')) autoMapping[header] = 'email';
            else if (lowerHeader.includes('first') && lowerHeader.includes('name')) autoMapping[header] = 'first_name';
            else if (lowerHeader.includes('last') && lowerHeader.includes('name')) autoMapping[header] = 'last_name';
            else if (lowerHeader.includes('company')) autoMapping[header] = 'company';
            else if (lowerHeader.includes('phone')) autoMapping[header] = 'phone';
            else if (lowerHeader.includes('location') || lowerHeader.includes('city')) autoMapping[header] = 'location';
            else if (lowerHeader.includes('country')) autoMapping[header] = 'country';
            else if (lowerHeader.includes('title') || lowerHeader.includes('job')) autoMapping[header] = 'job_title';
            else if (lowerHeader.includes('note')) autoMapping[header] = 'notes';
            else autoMapping[header] = '_skip';
          });
          setMapping(autoMapping);
          updatePreview(results.data.slice(0, 5), autoMapping);
        },
        error: () => {
          setCsvFileError('The CSV file could not be read.');
        }
      });
    };

    const handleFileUpload = (event) => {
      processCsvFile(event.target.files?.[0]);
      event.target.value = '';
    };

    const handleCsvDragEnter = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDraggingCsv(true);
    };

    const handleCsvDragOver = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'copy';
      setIsDraggingCsv(true);
    };

    const handleCsvDragLeave = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setIsDraggingCsv(false);
      }
    };

    const handleCsvDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDraggingCsv(false);

      const files = Array.from(event.dataTransfer.files || []);
      if (files.length !== 1) {
        setCsvFileError('Drop one CSV file at a time.');
        return;
      }

      processCsvFile(files[0]);
    };

    const updatePreview = (data, currentMapping) => {
      const mapped = data.map(row => {
        const mappedRow = {};
        Object.keys(currentMapping).forEach(csvCol => {
          const dbField = currentMapping[csvCol];
          if (dbField && dbField !== '_skip') {
            if (dbField === 'full_name') {
              // Split full name into first and last
              const { first_name, last_name } = splitFullName(row[csvCol]);
              if (first_name && first_name.trim()) mappedRow['first_name'] = first_name;
              if (last_name && last_name.trim()) mappedRow['last_name'] = last_name;
            } else if (dbField === 'location_country') {
              // Split location,country into location and country
              const { location, country } = splitLocationCountry(row[csvCol]);
              if (location && location.trim()) mappedRow['location'] = location;
              if (country && country.trim()) mappedRow['country'] = country;
            } else {
              const value = row[csvCol];
              if (value && value.trim()) {
                mappedRow[dbField] = value.trim();
              }
            }
          }
        });
        return mappedRow;
      });

      // Filter out columns that have no values across all preview rows
      if (mapped.length > 0) {
        const fieldsWithValues = new Set();
        mapped.forEach(row => {
          Object.keys(row).forEach(field => {
            if (row[field] && row[field].trim()) {
              fieldsWithValues.add(field);
            }
          });
        });

        const filteredMapped = mapped.map(row => {
          const filteredRow = {};
          fieldsWithValues.forEach(field => {
            filteredRow[field] = row[field] || '';
          });
          return filteredRow;
        });
        setPreview(filteredMapped);
      } else {
        setPreview(mapped);
      }
    };

    const handleMappingChange = (csvColumn, dbField) => {
      const newMapping = { ...mapping, [csvColumn]: dbField };
      setMapping(newMapping);
      updatePreview(showAllRecords ? csvData : csvData.slice(0, 5), newMapping);
    };

    const viewAllRecords = () => {
      setShowAllRecords(true);
      updatePreview(csvData, mapping);
    };

    const importToDatabase = async () => {
      setIsImporting(true);
      setImportResult(null);

      try {
        // Process the data into the format needed for the API
        const processedContacts = [];

        csvData.forEach(row => {
          const contact = {
            user_id: user?.id || 1
          };

          Object.keys(mapping).forEach(csvCol => {
            const dbField = mapping[csvCol];
            if (dbField && dbField !== '_skip') {
              if (dbField === 'full_name') {
                // Split full name into first and last
                const { first_name, last_name } = splitFullName(row[csvCol]);
                if (first_name && first_name.trim()) contact.first_name = first_name.trim();
                if (last_name && last_name.trim()) contact.last_name = last_name.trim();
              } else if (dbField === 'location_country') {
                // Split location,country into location and country
                const { location, country } = splitLocationCountry(row[csvCol]);
                if (location && location.trim()) contact.location = location.trim();
                if (country && country.trim()) contact.country = country.trim();
              } else {
                const value = row[csvCol];
                if (value && value.trim()) {
                  contact[dbField] = value.trim();
                }
              }
            }
          });

          // Add tags if provided
          if (tags && tags.trim()) {
            contact.tags = tags.trim();
          }


          // Only add contact if it has more than just user_id
          if (Object.keys(contact).length > 1) {
            processedContacts.push(contact);
          }
        });

        // Send to API
        const response = await axios.post('/api/contact-management/contacts/import', {
          contacts: processedContacts,
          leadStatus: leadStatus || 'cold' // Default to cold if none selected
        });

        setImportResult({
          success: true,
          message: `Successfully imported ${response.data.imported} contacts`,
          imported: response.data.imported,
          skipped: response.data.skipped || 0,
          skippedContacts: response.data.skippedContacts || []
        });

      } catch (error) {
        console.error('Import error:', error);
        setImportResult({
          success: false,
          message: error.response?.data?.message || 'Failed to import contacts. Please try again.',
          error: error.message
        });
      } finally {
        setIsImporting(false);
      }
    };

    // Handle force import for a single contact with duplicate email
    const handleForceImportSingle = async (contact, index) => {
      try {
        // Mark contact as importing
        const updatedSkippedContacts = [...importResult.skippedContacts];
        updatedSkippedContacts[index] = { ...contact, importing: true };
        setImportResult(prev => ({
          ...prev,
          skippedContacts: updatedSkippedContacts
        }));

        // Use the original contact data if available (preserves all processing including tags)
        const contactData = contact.originalContactData ?
          { ...contact.originalContactData, user_id: user?.id || 1 } :
          {
            user_id: user?.id || 1,
            first_name: contact.first_name || '',
            last_name: contact.last_name || '',
            email: contact.email || '',
            company: contact.company || '',
            job_title: contact.job_title || '',
            location: contact.location || '',
            country: contact.country || '',
            source: contact.source || 'manual',
            tags: contact.tags // Include any tags from the original processing
          };

        // Remove empty fields to avoid database issues (except for originalContactData case)
        if (!contact.originalContactData) {
          Object.keys(contactData).forEach(key => {
            if (contactData[key] === '' || contactData[key] === null || contactData[key] === undefined) {
              delete contactData[key];
            }
          });
        }

        // Ensure required fields
        if (!contactData.email) {
          throw new Error('Email is required for import');
        }
        contactData.user_id = user?.id || 1; // Always include user_id

        // Use the original lead status if available, otherwise use current selection
        const useLeadStatus = contact.leadStatus || leadStatus || 'cold';

        console.log('Force importing contact:', contactData);

        const response = await axios.post('/api/contact-management/contacts/import', {
          contacts: [contactData],
          leadStatus: useLeadStatus,
          forceImport: true // Flag to bypass duplicate checking
        });

        console.log('Force import response:', response.data);

        if (response.data.success) {
          // Remove contact from skipped list
          const filteredContacts = importResult.skippedContacts.filter((_, i) => i !== index);
          const newImported = (importResult.imported || 0) + 1;
          const newSkipped = Math.max((importResult.skipped || 0) - 1, 0);

          setImportResult(prev => ({
            ...prev,
            skippedContacts: filteredContacts,
            skipped: newSkipped,
            imported: newImported,
            message: `Successfully imported ${newImported} contacts`
          }));

          const contactName = contact.first_name && contact.last_name
            ? `${contact.first_name} ${contact.last_name}`.trim()
            : contact.name || contact.email;
          alert(`Successfully force imported: ${contactName}`);
        }
      } catch (error) {
        console.error('Force import error:', error);
        const contactName = contact.first_name && contact.last_name
          ? `${contact.first_name} ${contact.last_name}`.trim()
          : contact.name || contact.email;
        alert(`Failed to force import ${contactName}: ${error.response?.data?.error || error.message}`);

        // Reset importing state on error
        const updatedSkippedContacts = [...importResult.skippedContacts];
        updatedSkippedContacts[index] = { ...contact, importing: false };
        setImportResult(prev => ({
          ...prev,
          skippedContacts: updatedSkippedContacts
        }));
      }
    };

    // Handle bulk force import for all contacts with duplicate emails
    const handleForceImportBulk = async (duplicateContacts) => {
      if (!window.confirm(`Are you sure you want to force import ${duplicateContacts.length} duplicate contact${duplicateContacts.length !== 1 ? 's' : ''}? This will create new contact records even though contacts with these email addresses already exist.`)) {
        return;
      }

      try {
        // Mark all duplicate contacts as importing
        const updatedSkippedContacts = importResult.skippedContacts.map(contact =>
          duplicateContacts.find(dup => dup.email === contact.email && contact.reason === 'Duplicate email address')
            ? { ...contact, importing: true }
            : contact
        );
        setImportResult(prev => ({
          ...prev,
          skippedContacts: updatedSkippedContacts
        }));

        // Prepare contact data for force import - ensure all fields are included
        const contactsData = duplicateContacts.map(contact => {
          // Use the original contact data if available (preserves all processing including tags)
          const contactData = contact.originalContactData ?
            { ...contact.originalContactData, user_id: user?.id || 1 } :
            {
              user_id: user?.id || 1,
              first_name: contact.first_name || '',
              last_name: contact.last_name || '',
              email: contact.email || '',
              company: contact.company || '',
              job_title: contact.job_title || '',
              location: contact.location || '',
              country: contact.country || '',
              source: contact.source || 'manual',
              tags: contact.tags // Include any tags from the original processing
            };

          // Remove empty fields to avoid database issues (except for originalContactData case)
          if (!contact.originalContactData) {
            Object.keys(contactData).forEach(key => {
              if (contactData[key] === '' || contactData[key] === null || contactData[key] === undefined) {
                delete contactData[key];
              }
            });
          }

          // Ensure required fields
          contactData.user_id = user?.id || 1; // Always include user_id
          if (!contactData.email) {
            throw new Error(`Email is required for contact: ${contact.name || 'Unknown'}`);
          }

          return contactData;
        });

        // Determine the lead status to use - use the original if all contacts have the same leadStatus, otherwise use current selection
        const leadStatuses = duplicateContacts.map(c => c.leadStatus).filter(Boolean);
        const useLeadStatus = leadStatuses.length > 0 && leadStatuses.every(status => status === leadStatuses[0])
          ? leadStatuses[0]
          : leadStatus || 'cold';

        console.log('Bulk force importing contacts:', contactsData);

        const response = await axios.post('/api/contact-management/contacts/import', {
          contacts: contactsData,
          leadStatus: useLeadStatus,
          forceImport: true // Flag to bypass duplicate checking
        });

        console.log('Bulk force import response:', response.data);

        if (response.data.success) {
          // Remove successfully imported contacts from skipped list
          const duplicateEmails = duplicateContacts.map(c => c.email);
          const filteredContacts = importResult.skippedContacts.filter(contact =>
            !(duplicateEmails.includes(contact.email) && contact.reason === 'Duplicate email address')
          );

          const newImported = (importResult.imported || 0) + duplicateContacts.length;
          const newSkipped = Math.max((importResult.skipped || 0) - duplicateContacts.length, 0);

          setImportResult(prev => ({
            ...prev,
            skippedContacts: filteredContacts,
            skipped: newSkipped,
            imported: newImported,
            message: `Successfully imported ${newImported} contacts`
          }));

          alert(`Successfully force imported ${duplicateContacts.length} duplicate contact${duplicateContacts.length !== 1 ? 's' : ''}!`);
        }
      } catch (error) {
        console.error('Bulk force import error:', error);
        alert(`Failed to force import duplicates: ${error.response?.data?.error || error.message}`);

        // Reset importing state on error
        const updatedSkippedContacts = importResult.skippedContacts.map(contact =>
          duplicateContacts.find(dup => dup.email === contact.email && contact.reason === 'Duplicate email address')
            ? { ...contact, importing: false }
            : contact
        );
        setImportResult(prev => ({
          ...prev,
          skippedContacts: updatedSkippedContacts
        }));
      }
    };

    const handleManualImport = async (contactData) => {
      try {
        // Format contact data to match the existing import format
        const contactArray = [{
          ...contactData,
          user_id: user?.id,
          team_id: user?.teamId,
          contact_status: leadStatus || 'cold'
        }];

        const response = await axios.post('/api/contact-management/contacts/import', {
          contacts: contactArray,
          leadStatus: leadStatus || 'cold'
        });

        if (response.data.success) {
          alert('Contact imported successfully!');
        } else {
          throw new Error(response.data.error || 'Failed to import contact');
        }
      } catch (error) {
        console.error('Manual import error:', error);
        throw new Error(error.response?.data?.error || error.message || 'Failed to import contact');
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
        <div className="max-w-7xl mx-auto">
          {/* Manual Import Section */}
          <div className="bg-white rounded-lg shadow-xl p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-1">Manual Contact Import</h2>
                <p className="text-gray-600">Add a single contact manually to your database</p>
              </div>
              <button
                onClick={() => setShowManualImportModal(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 shadow-lg"
              >
                <Plus size={20} />
                Add Contact Manually
              </button>
            </div>
          </div>

          {/* CSV Contact Mapper */}
          <div className="bg-white rounded-lg shadow-xl p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">CSV Contact Mapper</h1>
                <p className="text-gray-600">Upload your CSV and map fields to your contacts database</p>
              </div>
              <MapPin className="w-12 h-12 text-indigo-500" />
            </div>

            {/* File Upload */}
            <div className="mb-8">
              <label
                onDragEnter={handleCsvDragEnter}
                onDragOver={handleCsvDragOver}
                onDragLeave={handleCsvDragLeave}
                onDrop={handleCsvDrop}
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all ${isDraggingCsv
                  ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                  : 'border-gray-300 hover:border-indigo-500 hover:bg-indigo-50'
                  }`}
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className={`w-10 h-10 mb-3 ${isDraggingCsv ? 'text-indigo-500' : 'text-gray-400'}`} />
                  <p className={`text-sm ${isDraggingCsv ? 'font-semibold text-indigo-700' : 'text-gray-600'}`}>
                    {isDraggingCsv ? (
                      'Drop the CSV file here'
                    ) : (
                      <><span className="font-semibold">Click to upload</span> or drag and drop</>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">CSV files only</p>
                </div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              {csvFileError && (
                <p className="mt-2 text-sm text-red-600" role="alert">{csvFileError}</p>
              )}
            </div>

            {/* Mapping Section */}
            {headers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Field Mapping</h2>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {headers.map((header, index) => (
                      <div key={header} className="flex items-center gap-4">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            CSV: Col {index + 1} - {header}
                          </label>
                          <input
                            type="text"
                            value={`Col ${index + 1} - ${header}`}
                            disabled
                            className="w-full px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-600"
                          />
                        </div>
                        <div className="text-gray-400">→</div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Database Field
                          </label>
                          <select
                            value={mapping[header] || '_skip'}
                            onChange={(e) => handleMappingChange(header, e.target.value)}
                            className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${mapping[header] && mapping[header] !== '_skip'
                              ? 'border-green-500 bg-green-50 text-green-800'
                              : 'border-gray-300'
                              }`}
                          >
                            {dbFields.map(field => (
                              <option key={field.value} value={field.value}>
                                {field.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tags Input Section */}
            {headers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Tag All Records</h2>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="max-w-md">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tag (optional)
                    </label>
                    <input
                      type="text"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      placeholder="Enter a tag to apply to all imported records"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      This tag will be applied to all imported records in the tags field
                    </p>
                  </div>

                  <div className="max-w-md mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lead Status (optional)
                    </label>
                    <select
                      value={leadStatus}
                      onChange={(e) => setLeadStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="">Select lead status...</option>
                      <option value="cold">Cold</option>
                      <option value="warm">Warm</option>
                      <option value="active">Active</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      This status will be applied to all imported records
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Section */}
            {preview.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">
                  {showAllRecords ? `All Records (${preview.length})` : `Preview (First 5 Records)`}
                </h2>
                <div className="bg-gray-50 rounded-lg p-6 overflow-x-auto">
                  {Object.keys(preview[0]).length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          {Object.keys(preview[0]).map(field => (
                            <th key={field} className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider bg-gray-100 max-w-xs">
                              {field}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {preview.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            {Object.values(row).map((value, vIdx) => (
                              <td key={vIdx} className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={value || ''}>
                                {value || <span className="text-gray-400 italic">empty</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No data to preview. All mapped columns appear to be empty or skipped.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* View All Button */}
            {csvData.length > 0 && !showAllRecords && (
              <div className="flex gap-4 mb-8">
                <button
                  onClick={viewAllRecords}
                  className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  <Eye className="w-5 h-5" />
                  View All Records ({csvData.length} total)
                </button>
              </div>
            )}

            {/* Import to Database Button */}
            {csvData.length > 0 && showAllRecords && !importResult?.success && (
              <div className="flex gap-4 mb-8">
                <button
                  onClick={importToDatabase}
                  disabled={isImporting}
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isImporting ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Import to Database ({csvData.length} records)
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Import Result */}
            {importResult && (
              <div className={`mb-8 p-4 rounded-lg border ${importResult.success
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                <div className="flex items-center gap-2">
                  {importResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <p className="font-medium">{importResult.message}</p>
                </div>
                {importResult.success && importResult.skipped > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700">
                      {importResult.skipped} records were skipped
                    </p>
                    {console.log(importResult.skippedContacts)}
                    {importResult.skippedContacts && importResult.skippedContacts.length > 0 && (
                      <div className="mt-2">
                        <details className="text-sm">
                          <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                            View skipped contacts ({importResult.skippedContacts.length})
                          </summary>
                          <div className="mt-2 bg-gray-50 rounded border overflow-hidden">
                            {/* Table for skipped contacts */}
                            <div className="overflow-x-auto max-h-60 overflow-y-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Name
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Email
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Company
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Tags / Lead Status
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Reason
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {importResult.skippedContacts.map((contact, index) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                                        <span className="font-medium text-gray-900">
                                          {contact.first_name || contact.last_name
                                            ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
                                            : contact.name || '-'
                                          }
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                        {contact.email || '-'}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                                        {contact.company || '-'}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${contact.reason === 'Duplicate email address'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : contact.reason?.includes('Database error')
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                          }`}>
                                          {contact.reason || 'Unknown'}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                                        {contact.reason === 'Duplicate email address' && (
                                          <button
                                            onClick={() => handleForceImportSingle(contact, index)}
                                            disabled={contact.importing}
                                            className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                          >
                                            {contact.importing ? (
                                              <>
                                                <RefreshCw size={10} className="animate-spin" />
                                                Importing...
                                              </>
                                            ) : (
                                              <>
                                                <RefreshCw size={10} />
                                                Import Anyway
                                              </>
                                            )}
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Bulk force import button for duplicate email addresses */}
                            {(() => {
                              const duplicateContacts = importResult.skippedContacts.filter(
                                contact => contact.reason === 'Duplicate email address'
                              );
                              return duplicateContacts.length > 0 && (
                                <div className="p-3 border-t border-gray-200 bg-white">
                                  <button
                                    onClick={() => handleForceImportBulk(duplicateContacts)}
                                    disabled={duplicateContacts.some(c => c.importing)}
                                    className="px-3 py-2 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                  >
                                    <RefreshCw size={12} />
                                    Import All Duplicates Anyway ({duplicateContacts.length})
                                  </button>
                                </div>
                              );
                            })()}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Tips for Mapping</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• The tool auto-detects common field names</li>
                  <li>• You can manually adjust any mapping using the dropdowns</li>
                  <li>• Select "Skip Column" for fields you don't want to import</li>
                  <li>• Use "Full Name (split into first/last)" to automatically split names</li>
                  <li>• Name splitting: "John Doe" → first: "John", last: "Doe"</li>
                  <li>• Multi-word last names: "John Van Der Berg" → first: "John", last: "Van Der Berg"</li>
                  <li>• Use "Location,Country (split into location/country)" for combined data</li>
                  <li>• Location splitting: "ca, usa" → location: "ca", country: "usa"</li>
                  <li>• Empty values will not be displayed in the preview</li>
                  <li>• Use "View All Records" to see your complete dataset</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Manual Import Modal */}
        <ManualImportModal
          isOpen={showManualImportModal}
          onClose={() => setShowManualImportModal(false)}
          onImport={handleManualImport}
        />
      </div>
    );
  };

  const Sidebar = () => (
    <div className="w-64 bg-slate-900 text-white h-screen flex flex-col">
      <div className="p-6 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <img src={logoWhite} alt="PromptCRM: Smart Email Management" className="h-11 w-11" />
          <h1 className="text-xl font-bold">PromptCRM: Smart Email Management</h1>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <div className="space-y-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'dashboard' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <BarChart3 size={20} />
            <span>Producer Dashboard</span>
          </button>

          <button
            onClick={() => setActiveTab('leads')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'leads' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Target size={20} />
            <span>Linkedin Scraping</span>
          </button>

          <button
            onClick={() => setActiveTab('preview')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'preview' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Eye size={20} />
            <span>Scraping Preview</span>
          </button>


          <button
            onClick={() => setActiveTab('campaigns')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'campaigns' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Mail size={20} />
            <span>Email Campaigns</span>
          </button>

          <button
            onClick={() => {
              setCampaignToOpen(null);
              setActiveTab('campaign-calendar');
            }}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'campaign-calendar' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <CalendarDays size={20} />
            <span>Campaigns Calendar</span>
          </button>

          <button
            onClick={() => setActiveTab('templates')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'templates' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <FileText size={20} />
            <span>Email Templates</span>
          </button>

          <button
            onClick={() => setActiveTab('contacts')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'contacts' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Users size={20} />
            <span>Contact Management</span>
          </button>

          <button
            onClick={() => setActiveTab('import-contacts')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'import-contacts' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Upload size={20} />
            <span>Import Contacts</span>
          </button>

          <button
            onClick={() => setActiveTab('import-templates')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'import-templates' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Upload size={20} />
            <span>Import Templates</span>
          </button>

          <button
            onClick={() => setActiveTab('assistant')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'assistant' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Bot size={20} />
            <span>Assistant</span>
          </button>

          <button
            onClick={() => setActiveTab('responses')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'responses' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <RefreshCw size={20} />
            <span>Response Handler</span>
          </button>

          <button
            onClick={() => setActiveTab('forms')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'forms' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Database size={20} />
            <span>Website Forms</span>
          </button>

          <button
            onClick={() => setActiveTab('settings')}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-blue-600' : 'hover:bg-slate-800'
              }`}
          >
            <Settings size={20} />
            <span>Settings</span>
          </button>
        </div>
      </nav>
    </div>
  );

  const renderActiveTab = () => {
    console.log('Rendering tab:', activeTab);
    switch (activeTab) {
      case 'dashboard':
        return <ProducerDashboard
          currentProducer={currentProducer}
          stats={stats}
          campaigns={campaigns}
          getProducerColor={getProducerColor}
        />;
      case 'campaigns':
        return <EmailCampaigns
          getProducerColor={getProducerColor}
          gmailStatus={gmailStatus}
          initialCampaign={campaignToOpen}
          onInitialCampaignHandled={handleCalendarCampaignOpened}
        />;
      case 'campaign-calendar':
        return <CampaignCalendar onOpenCampaign={handleOpenCalendarCampaign} />;
      case 'templates':
        return <EmailTemplates />;
      case 'contacts':
        return <ContactManagement
          producers={producers}
          getProducerColor={getProducerColor}
          user={user}
        />;
      case 'import-contacts':
        return <ImportContacts />;
      case 'import-templates':
        return <ImportEmailTemplates />;
      case 'assistant':
        return <AssistantWorkspace />;
      case 'responses':
        return <ResponseHandler gmailStatus={gmailStatus} />;
      case 'forms':
        return <WebsiteForms
          websiteForms={websiteForms}
          getProducerColor={getProducerColor}
        />;
      case 'preview':
        return <ScrapingPreview gmailStatus={gmailStatus} />;
      case 'settings':
        return <SettingsPage
          gmailStatus={gmailStatus}
          checkGmailStatus={checkGmailStatus}
          connectToGmail={connectToGmail}
          zohoConnected={zohoConnected}
          connectToZoho={connectToZoho}
          setGmailStatus={setGmailStatus}
        />;
      case 'privacy':
        return (
          <div className="max-w-4xl mx-auto">
            <PrivacyPolicyContent />
          </div>
        );
      case 'terms':
        return (
          <div className="max-w-4xl mx-auto">
            <TermsOfServiceContent />
          </div>
        );
      default:
        return <ProducerDashboard
          currentProducer={currentProducer}
          stats={stats}
          campaigns={campaigns}
          getProducerColor={getProducerColor}
        />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <Header />
        <div className="p-6">
          {/* Always render LeadSources but only show when leads tab is active */}
          <div style={{ display: activeTab === 'leads' ? 'block' : 'none' }}>
            <LeadSources />
          </div>

          {/* Render other tabs using the existing renderActiveTab function */}
          {activeTab !== 'leads' && renderActiveTab()}
        </div>
      </div>

      {/* Global Scraping Preview Modal - always available */}
      <ScrapingPreviewModal
        isOpen={showGlobalPreviewModal}
        onClose={handleCloseGlobalModal}
        previewData={globalPreviewData}
        onConfirm={handleConfirmGlobalLeads}
        isConfirming={isConfirmingGlobal}
      />
    </div>
  );
};

export default EmailMarketingPortal;
